/**
 * Converting between a *wall clock* ("2026-09-24", "10:30") in a named IANA
 * timezone and the *instant* it denotes.
 *
 * Why this exists (NG-1791): an event carries its own timezone — the zone its
 * wall-clock times are defined in, per the backend's `Event.timezone`. Building
 * that instant with the browser's `Date` applies the *viewer's* offset instead,
 * so a Chicago author picking 10:30 with "Asia/Seoul" selected stored 10:30
 * Chicago labelled Seoul. The error is invisible wherever a surface echoes the
 * entered wall clock back, and doubles wherever one converts properly.
 *
 * Nothing here reads the host timezone. Callers that mean "the viewer's zone"
 * pass it explicitly, so the zone in play is always visible at the call site.
 */

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

/** Wall-clock parts of an instant, as the zero-padded strings our inputs use. */
export type TWallClock = {
  /** "YYYY-MM-DD" */
  date: string;
  /** "HH:MM", 24-hour */
  time: string;
};

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * Formatter per zone. Constructing an `Intl.DateTimeFormat` is the expensive
 * part of every conversion below, and resolving one wall clock needs several —
 * so they are cached rather than rebuilt per call.
 *
 * A `null` entry caches a *rejected* zone, so an unknown name stays one cheap
 * map lookup instead of throwing through `Intl` on every render.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat | null>();

/**
 * Empty the formatter cache.
 *
 * The cache is a pure memo — the same zone name always yields the same
 * formatter, so nothing observable depends on whether an entry is present. The
 * one exception is the `console.warn` for an unknown zone, which fires once per
 * name rather than once per call, so a test asserting that warning needs to
 * start from a known-empty cache. That is the seam; production never calls it.
 */
export function resetZoneFormatterCache(): void {
  formatterCache.clear();
}

function zoneFormatter(timeZone: string): Intl.DateTimeFormat | null {
  const cached = formatterCache.get(timeZone);
  if (cached !== undefined) return cached;
  let formatter: Intl.DateTimeFormat | null = null;
  try {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  } catch (error) {
    // An unsupported or misspelled IANA name. Callers get null and fall back;
    // warn rather than error since a stale saved zone is recoverable.
    console.warn("[zoned-time] unknown IANA timezone", { timeZone, error });
    formatter = null;
  }
  formatterCache.set(timeZone, formatter);
  return formatter;
}

/**
 * The wall-clock components of `instant` in `timeZone`, as the milliseconds a
 * UTC clock would read if it showed those same components.
 *
 * Comparing that against the real instant is what yields the zone's offset, and
 * it is the one primitive `Intl` gives us — there is no API that hands back a
 * zone's offset directly.
 */
function wallClockMsInZone(instantMs: number, timeZone: string): number | null {
  const formatter = zoneFormatter(timeZone);
  if (!formatter) return null;
  const parts = formatter.formatToParts(new Date(instantMs));
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find((p) => p.type === type)?.value;
    return value === undefined ? NaN : Number(value);
  };
  const year = read("year");
  const month = read("month");
  const day = read("day");
  // `hour12: false` still renders midnight as "24" in some engines; normalise it
  // so a midnight wall clock does not land a day late.
  const hour = read("hour") % 24;
  const minute = read("minute");
  const second = read("second");
  if ([year, month, day, hour, minute, second].some(Number.isNaN)) return null;
  return Date.UTC(year, month - 1, day, hour, minute, second);
}

/**
 * Parse "YYYY-MM-DD" + "HH:MM" into the ms a UTC clock showing them would read.
 *
 * The date is validated by round-tripping the built instant, which rejects every
 * out-of-range calendar value on its own — month 13 moves the year, "Feb 30"
 * moves the month. Only the clock needs its own check, because a rolled time
 * ("10:99" becoming 11:39) lands on the same date and survives that round trip.
 */
function parseWallClock(date: string, time: string): number | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  const t = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!d || !t) return null;
  const [year, month, day] = [Number(d[1]), Number(d[2]), Number(d[3])];
  const [hour, minute] = [Number(t[1]), Number(t[2])];
  if (hour > 23 || minute > 59) return null;
  const ms = Date.UTC(year, month - 1, day, hour, minute);
  const back = new Date(ms);
  const inRange =
    back.getUTCFullYear() === year && back.getUTCMonth() === month - 1 && back.getUTCDate() === day;
  return inRange ? ms : null;
}

/**
 * The instant at which `timeZone`'s clocks read `date` `time`.
 *
 * Daylight-saving transitions make that mapping non-unique, and this follows the
 * disambiguation every calendar uses (and which `Temporal` calls "compatible"):
 *
 * - **Ambiguous** — the wall clock happens twice, when clocks go back. Takes the
 *   *first* occurrence, so an event does not silently jump an hour later.
 * - **Skipped** — the wall clock never happens, when clocks go forward. Shifts
 *   *forward* by the length of the gap, landing just after the jump, so an event
 *   cannot be scheduled into an hour that does not exist.
 *
 * Returns null when the date, the time, or the timezone cannot be resolved —
 * never a silently host-zone-converted instant, which is the bug this replaces.
 */
export function zonedWallClockToInstant(date: string, time: string, timeZone: string): Date | null {
  const wallMs = parseWallClock(date, time);
  if (wallMs === null) return null;

  // Probe the offset a day either side, which brackets any real transition. The
  // two are equal whenever there is no transition in range, which is almost
  // always, and then the first candidate below settles it.
  const beforeWall = wallClockMsInZone(wallMs - MS_PER_DAY, timeZone);
  const afterWall = wallClockMsInZone(wallMs + MS_PER_DAY, timeZone);
  if (beforeWall === null || afterWall === null) return null;
  const offsetBefore = beforeWall - (wallMs - MS_PER_DAY);
  const offsetAfter = afterWall - (wallMs + MS_PER_DAY);

  /** The instant this offset implies, but only if the zone agrees with it. */
  const candidate = (offset: number): number | null => {
    const instantMs = wallMs - offset;
    return wallClockMsInZone(instantMs, timeZone) === wallMs ? instantMs : null;
  };

  // `offsetBefore` is the larger offset, so the instant it implies is the
  // earlier one — trying it first *is* the "first occurrence" rule for an
  // ambiguous clock, with no sorting needed. In the gap neither agrees, and
  // reading the clock with the pre-transition offset lands past the jump, which
  // is exactly the forward shift we want.
  return new Date(candidate(offsetBefore) ?? candidate(offsetAfter) ?? wallMs - offsetBefore);
}

/** Format a whole-minute offset as the ISO 8601 "+HH:MM" / "-HH:MM" suffix. */
function offsetSuffix(offsetMinutes: number): string {
  const sign = offsetMinutes < 0 ? "-" : "+";
  const abs = Math.abs(offsetMinutes);
  return `${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

/**
 * The wall clock of `instant` in `timeZone` — the inverse of
 * {@link zonedWallClockToInstant}, and what a surface needs to show an event at
 * the time its own zone calls it. Returns null if either input is unusable.
 */
export function wallClockInZone(instant: Date | string, timeZone: string): TWallClock | null {
  const instantMs = typeof instant === "string" ? Date.parse(instant) : instant.getTime();
  if (Number.isNaN(instantMs)) return null;
  const wallMs = wallClockMsInZone(instantMs, timeZone);
  if (wallMs === null) return null;
  const [date, time] = formatWallClock(wallMs).split("T");
  return { date, time };
}

/** A wall-clock ms value as "YYYY-MM-DDTHH:MM". */
function formatWallClock(wallMs: number): string {
  const d = new Date(wallMs);
  const date = `${String(d.getUTCFullYear()).padStart(4, "0")}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  return `${date}T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

/**
 * Re-state `instant` at the same wall clock, but in a different zone: read the
 * clock `from`'s users see, then resolve that clock in `to`. This is what
 * "the meeting is at 10:30, and actually that's Seoul not Chicago" means — the
 * clock stays put and the instant moves. Returns null if either zone is unknown.
 */
export function restateInZone(instant: Date | string, from: string, to: string): string | null {
  const wall = wallClockInZone(instant, from);
  return wall && toZonedISOString(wall.date, wall.time, to);
}

/**
 * `instant`, as the offset-stamped ISO 8601 string the events API stores, stated
 * in `timeZone` — so the wall clock a reader sees in the payload is the one the
 * event's own zone calls it, and the offset beside it is that zone's.
 *
 * Returns null for an unusable instant or an unknown zone.
 */
function instantToZonedISOString(instant: Date, timeZone: string): string | null {
  const instantMs = instant.getTime();
  if (Number.isNaN(instantMs)) return null;
  // One zone read answers both halves: the wall clock is what it returns, and
  // the offset is how far that clock sits from the instant itself.
  const wallMs = wallClockMsInZone(instantMs, timeZone);
  if (wallMs === null) return null;
  const offsetMinutes = (wallMs - instantMs) / MS_PER_MINUTE;
  return `${formatWallClock(wallMs)}:00${offsetSuffix(offsetMinutes)}`;
}

/**
 * `date` `time` in `timeZone`, as an offset-stamped ISO 8601 string for the API.
 *
 * The offset is the zone's own at that instant, so the string denotes the time
 * the author actually chose. Its wall-clock half is re-derived from the resolved
 * instant rather than echoed from the input, because a daylight-saving gap moves
 * it — writing the entered clock beside a shifted offset would name a third,
 * unintended time.
 *
 * Returns null when the inputs cannot be resolved; callers must not fall back to
 * a host-zone conversion, which is the defect this exists to prevent.
 */
export function toZonedISOString(date: string, time: string, timeZone: string): string | null {
  const instant = zonedWallClockToInstant(date, time, timeZone);
  if (!instant) return null;
  return instantToZonedISOString(instant, timeZone);
}

/**
 * `instant` as the value of a `datetime-local` input — "YYYY-MM-DDTHH:MM" — read
 * in `timeZone`, or `""` when either input is unusable.
 *
 * The zoned counterpart of `toLocalDateTimeInput` (`helpers/issue.helper`),
 * which reads the *host* clock. A surface bound to an entity that carries its
 * own zone wants this one: the two agree only for a viewer who happens to sit in
 * that zone, and silently disagree for everyone else (NG-1886).
 */
export function toZonedDateTimeLocal(
  instant: Date | string | null | undefined,
  timeZone: string,
): string {
  if (!instant) return "";
  const wall = wallClockInZone(instant, timeZone);
  return wall ? `${wall.date}T${wall.time}` : "";
}

/**
 * The inverse: the instant at which `timeZone`'s clocks read a `datetime-local`
 * input's "YYYY-MM-DDTHH:MM" value. Null for an unparseable value or an unknown
 * zone — never a host-zone conversion, which is the defect this exists to avoid.
 */
export function zonedDateTimeLocalToInstant(value: string, timeZone: string): Date | null {
  const [date, time] = value.split("T");
  if (!date || !time) return null;
  return zonedWallClockToInstant(date, time, timeZone);
}

/** Preserve a stored repeated DST hour until the user changes its event-zone clock. */
export function resolveZonedInstant(
  date: string,
  time: string,
  timeZone: string,
  savedInstant?: string | null,
): Date | null {
  const savedWall = savedInstant ? wallClockInZone(savedInstant, timeZone) : null;
  if (savedInstant && savedWall?.date === date && savedWall.time === time)
    return new Date(savedInstant);
  return zonedWallClockToInstant(date, time, timeZone);
}
